import { EMPTY_STRING } from "@regrello/core-utils";
import type { RegrelloFieldInputProps, RegrelloNumericInputProps } from "@regrello/ui-core";
import { RegrelloField, type RegrelloInputProps, RegrelloNumericInput, type RegrelloSize } from "@regrello/ui-core";
import React, { useId } from "react";

import type { RegrelloFormFieldBaseProps } from "./_internal/RegrelloFormFieldBaseProps";
import { RegrelloFormFieldEmptyValueNone } from "./_internal/RegrelloFormFieldEmptyValueNone";
import { RegrelloFormFieldLayout } from "./_internal/RegrelloFormFieldLayout";
import { getSelfContainedFormInternal } from "./_internal/selfContainedForm/getSelfContainedFormInternal";

export interface RegrelloFormFieldNumberProps
  extends RegrelloFormFieldBaseProps<number | string>,
    Pick<RegrelloInputProps, "autoFocus" | "onBlur" | "onChange" | "onKeyDownCapture" | "inputRef" | "placeholder">,
    Pick<RegrelloNumericInputProps, "hideIncrementButtons"> {
  /**
   * If provided, this will decorate the number form field with the currency symbol.
   */
  currencySymbol?: string;

  /**
   * The numeric prefix string to seed into the input on mount (e.g., "-", locale decimal, or
   * "-."). If provided, this takes precedence over the default value or value.
   *
   * @default undefined
   */
  defaultStringPrefixValue?: string;

  /**
   * The initial value to show in the number field. If you want to use a controlled value, just pass
   * {@see value} instead.
   */
  defaultValue?: number;

  /**
   * Formatting options for the value displayed in the number field. This also affects what
   * characters are allowed to be typed by the user.
   */
  formatOptions?: Intl.NumberFormatOptions | undefined;

  /** Additional class name to apply to the input element. */
  inputClassName?: string;

  /**
   * Whether to hide the form field error icon and message. Added (potentially temporarily depending
   * on Design System updates) for Conditional Branching to support the new error state for stage
   * start date forms.
   */
  isErrorMessageHidden?: boolean;

  /**
   * The minimum value to enforce. If `max` is provided and the user types a value greater than
   * `max`, the field will immediately replace the value with the `max` value.
   */
  max?: number;

  /**
   * The minimum value to enforce. If `min` is provided and the user types a value less that `min`,
   * the field will immediately replace the value with the `min` value.
   */
  min?: number;

  /**
   * If provided, will show an 'x' button in the input that invokes this callback when clicked.
   * Expected usage thereafter is for the callback to clear the input's value.
   */
  onClearClick?: () => void;

  /**
   * Callback invoked when the input value changes. The value is returned as both a parsed number
   * and as a string. Note that the parsed number may be `null` if the string is not a valid number.
   */
  onValueChange?: (value: number | null) => void;

  /**
   * The size of the input field.
   *
   * @default RegrelloSize.LARGE
   */
  size?: RegrelloSize;

  /**
   * Whether this input should suppress decimal formatting on initial interaction.
   * If true, formatting will only be applied on blur or on incrementing / decrementing via the stepper buttons.
   *
   * @default false
   */
  suppressDecimalFormattingOnInitialInteraction?: boolean;

  /**
   * The current field value. Leave this undefined to use this component in uncontrolled mode. Pass
   * a controlled value of null to indicate that the field is empty.
   */
  value?: number | null;
}

export const RegrelloFormFieldNumber = React.memo(function RegrelloFormFieldNumberFn({
  className,
  currencySymbol,
  dataTestId,
  defaultStringPrefixValue,
  defaultValue,
  endAdornment,
  error,
  formatOptions,
  helperText,
  hideIncrementButtons,
  infoTooltipIconName,
  infoTooltipText,
  infoTooltipVariant,
  inputClassName,
  isDefaultMarginsOmitted,
  isDeleted,
  isEmphasized,
  isErrorMessageHidden = false,
  isLabelVerticallyCentered,
  isRequiredAsteriskShown,
  label,
  labelPlacement,
  labelWidth,
  max,
  min,
  onBlur,
  onChange: propsOnChange,
  onValueChange,
  selfContainedForm,
  size,
  suppressDecimalFormattingOnInitialInteraction = false,
  value,
  variant = "default",
  ...textFieldProps
}: RegrelloFormFieldNumberProps) {
  const id = useId();

  if (max != null && min != null && max < min) {
    throw new Error(`Invalid props: max (${max}) cannot be less than min (${min})`);
  }

  const startElement =
    currencySymbol != null
      ? {
          type: "nonInteractive" as const,
          element: <span>{currencySymbol}</span>,
        }
      : undefined;

  const renderInput = (fieldInputProps?: RegrelloFieldInputProps) => (
    <RegrelloNumericInput
      {...textFieldProps}
      {...fieldInputProps}
      autoFocus={textFieldProps.autoFocus || selfContainedForm != null}
      className={inputClassName}
      // HACKHACK (clewis): We intentionally don't use controlled mode! RegrelloNumericInput is
      // tricky because its underlying library uses an uncontrolled value that is emitted (via
      // onChange) only on blur or on increment.
      //
      // Passing in a controlled value after a round-trip autosave can "clobber" this uncontrolled
      // value while the user is typing, leading to much frustration and confusion.
      //
      // Thus we pass in `value` as a `defaultValue` and then operate uncontrolled thereafter,
      // listening for onChange and letting the input keep track of its own value.
      defaultStringPrefixValue={defaultStringPrefixValue}
      defaultValue={value ?? defaultValue}
      formatOptions={formatOptions}
      hideIncrementButtons={hideIncrementButtons}
      id={id}
      intent={error != null ? "danger" : isEmphasized ? "warning" : undefined}
      maxValue={max}
      minValue={min}
      onBlur={(event) => onBlur?.(event as React.FocusEvent<HTMLInputElement>)}
      onChange={onValueChange}
      size={size}
      startElement={startElement}
      suppressDecimalFormattingOnInitialInteraction={suppressDecimalFormattingOnInitialInteraction}
    />
  );

  if (variant === "spectrum" && selfContainedForm == null) {
    return (
      <RegrelloField
        dataTestId={dataTestId}
        deleted={isDeleted}
        description={infoTooltipText}
        endAdornment={endAdornment}
        errorMessage={error}
        helperText={typeof helperText === "string" ? helperText : undefined}
        htmlFor={id}
        label={label}
        name={textFieldProps.name ?? EMPTY_STRING}
        required={isRequiredAsteriskShown}
      >
        {(fieldInputProps) => renderInput(fieldInputProps)}
      </RegrelloField>
    );
  }

  return (
    <RegrelloFormFieldLayout
      className={className}
      dataTestId={dataTestId}
      endAdornment={endAdornment}
      error={isErrorMessageHidden ? undefined : error}
      helperText={helperText}
      htmlFor={id}
      infoTooltipIconName={infoTooltipIconName}
      infoTooltipText={infoTooltipText}
      infoTooltipVariant={infoTooltipVariant}
      isDefaultMarginsOmitted={isDefaultMarginsOmitted}
      isDeleted={isDeleted}
      isLabelVerticallyCentered={isLabelVerticallyCentered}
      isRequiredAsteriskShown={isRequiredAsteriskShown}
      label={label}
      labelPlacement={labelPlacement}
      labelWidth={labelWidth}
      selfContainedForm={getSelfContainedFormInternal(selfContainedForm, (savedValue) =>
        savedValue == null || savedValue === EMPTY_STRING ? <RegrelloFormFieldEmptyValueNone /> : savedValue,
      )}
      variant={variant}
    >
      {renderInput()}
    </RegrelloFormFieldLayout>
  );
});
